import { useMemo } from "react";
import { useWorkorderContext } from "../../../../../context/WorkorderContext";
import HoseItem from "../../../../../components/workorders/hoses/HoseItem";
import EmptyHosesState from "../../../../../components/workorders/hoses/EmptyHosesState";

/**
 */
interface HosesSectionProps {
  workorderId: string;
  machineId?: string;
  onRemoveHose: (hoseId: string) => void;
  targetType?: "workorder" | "machine";
  canEdit?: boolean;
}

/**
 * Composant qui affiche et gère la liste des flexibles pour un workorder ou une machine
 * 
 * @param workorderId - ID du workorder
 * @param machineId - ID de la machine (vide si les flexibles sont liés au workorder)
 * @param onRemoveHose - Fonction de suppression d'un flexible
 * @param targetType - Type de contexte (workorder ou machine)
 */
export default function HosesSection({
  workorderId,
  machineId = "",
  onRemoveHose,
  targetType = "workorder",
  canEdit = true,
}: HosesSectionProps) {
  // Récupérer le contexte pour avoir les données à jour
  const { workorder } = useWorkorderContext();

  // Utiliser directement les hoses du contexte sans état local
  const hosesToDisplay = useMemo(() => {
    let hosesToUse: EasyTech.WorkorderHose[] = [];

    if (targetType === "machine" && machineId) {
      // Récupérer la machine du workorder par son ID
      const machine = workorder?.machines?.find((m) => m.id === machineId);

      if (machine && machine.hoses && machine.hoses.length > 0) {
        // Si la machine a des hoses directement dans sa propriété hoses
        hosesToUse = machine.hoses;
      } else {
        // Sinon, chercher les hoses qui ont cette machine comme parent
        const hosesWithMachine =
          workorder?.hoses?.filter((h) => h.machine?.id === machineId) || [];
        hosesToUse = hosesWithMachine;
      }
    } else {
      // Récupérer les hoses du workorder qui ne sont pas liées à une machine
      // IMPORTANT: Filtrer pour exclure les hoses qui sont liés à une machine quelconque
      hosesToUse =
        workorder?.hoses?.filter((h) => {
          // Exclure les hoses qui ont une machine associée
          if (h.machine && h.machine.id) return false;
          
          // Vérifier également que le hose n'existe pas dans une machine
          const existsInMachine = workorder?.machines?.some(machine => 
            machine.hoses?.some(mHose => mHose.id === h.id)
          );
          
          return !existsInMachine;
        }) || [];
    }

    // Trier les hoses par date de création (du plus récent au plus ancien)
    const sortedHoses = [...hosesToUse].sort((a, b) => {
      const dateA = new Date(a.created || a.hose?.created || 0).getTime();
      const dateB = new Date(b.created || b.hose?.created || 0).getTime();
      return dateB - dateA;
    });

    // Éliminer les doublons en ne gardant que la première occurrence de chaque ID
    const uniqueSortedHoses = sortedHoses.filter(
      (hose, index, self) => index === self.findIndex((h) => h.id === hose.id)
    );

    return uniqueSortedHoses;
  }, [workorder, targetType, machineId]);

  // Si aucun hose n'est trouvé, afficher un message
  if (hosesToDisplay.length === 0) {
    return <EmptyHosesState />;
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      {hosesToDisplay.map((hose) => (
        <HoseItem
          key={hose.id}
          workorderHose={hose}
          workorderId={workorderId}
          machineId={machineId}
          onRemoveHose={onRemoveHose}
          canEdit={canEdit}
        />
      ))}
    </div>
  );
}
